<template>
  <div class="pmw">
    <div v-if="loading && !data" class="pmw-state">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      {{ t("searching") }}
    </div>
    <p v-else-if="status === 'no_key'" class="pmw-state">{{ $t("webProfile.noKey") }}</p>
    <p v-else-if="failed && !found" class="pmw-state text-danger">{{ $t("webProfile.error") }}</p>
    <p v-else-if="data && !found" class="pmw-state">{{ t("notFound") }}</p>

    <template v-if="found">
      <p v-if="section === 'summary'" class="pmw-summary">{{ m.summary || t("emptySection") }}</p>

      <template v-else>
        <p v-if="!items.length" class="pmw-state">{{ t("emptySection") }}</p>
        <ol v-else-if="section === 'exporters' || section === 'importers'" class="pmw-rank">
          <li v-for="x in items" :key="x.name"><strong>{{ x.name }}</strong><span v-if="x.detail"> — {{ x.detail }}</span></li>
        </ol>
        <ul v-else-if="section === 'prices'" class="pmw-plain">
          <li v-for="(x, i) in items" :key="i">{{ x.text }}<span v-if="x.period" class="pmw-muted"> · {{ x.period }}</span></li>
        </ul>
        <ul v-else-if="section === 'trends'" class="pmw-plain">
          <li v-for="(x, i) in items" :key="i">{{ x }}</li>
        </ul>
        <ul v-else-if="section === 'companies'" class="pmw-plain">
          <li v-for="x in items" :key="x.name">
            <strong>{{ x.name }}</strong><span v-if="x.country" class="pmw-muted"> · {{ x.country }}</span>
            <span v-if="x.detail"> — {{ x.detail }}</span>
          </li>
        </ul>
      </template>
    </template>

    <!-- Fecha y fuentes: todas las secciones salen de la misma búsqueda. -->
    <div v-if="data && data.fetchedAt && status !== 'no_key'" class="pmw-foot">
      <span class="pmw-muted">
        {{ t("fromWeb", { date: formatDate(data.fetchedAt) }) }}
        <template v-if="(data.sources || []).length">
          · <a href="#" @click.prevent="showSources = !showSources">{{ $t("webProfile.sources", { n: data.sources.length }) }}</a>
        </template>
      </span>
    </div>
    <ul v-if="showSources" class="pmw-plain pmw-sources">
      <li v-for="s in data.sources" :key="s.url">
        <a :href="s.url" target="_blank" rel="noopener noreferrer">{{ s.title }}</a>
        <span class="pmw-muted"> {{ s.source }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * Un widget de mercado de la ficha del producto: una sección (resumen, exportadores,
 * importadores, precios, tendencias, empresas) de lo que se encontró en la web. Todas
 * leen la misma búsqueda (productMarketStore.js): solo se pide si alguno está en la
 * pestaña, y el back busca una vez por producto. «Volver a buscar» va en el menú.
 */
import { loadMarket, marketState } from "./productMarketStore";

export const MARKET_SECTIONS = ["summary", "exporters", "importers", "prices", "trends", "companies"];

export default {
  name: "ProductMarketWidget",
  props: {
    itemId: { type: String, required: true },
    section: { type: String, default: "summary" },
  },
  data() {
    return { showSources: false };
  },
  computed: {
    st() {
      return marketState(this.itemId) || {};
    },
    data() {
      return this.st.data || null;
    },
    loading() {
      return !!this.st.loading;
    },
    failed() {
      return !!this.st.failed;
    },
    status() {
      return (this.data && this.data.status) || "";
    },
    found() {
      return !!(this.data && this.data.found);
    },
    m() {
      return (this.data && this.data.profile) || {};
    },
    items() {
      return Array.isArray(this.m[this.section]) ? this.m[this.section] : [];
    },
  },
  watch: {
    itemId() {
      this.showSources = false;
      this.load(false);
    },
  },
  mounted() {
    this.load(false);
  },
  methods: {
    t(key, params) {
      return this.$t(`productWidgets.market.${key}`, params);
    },
    load(force) {
      return loadMarket(this.itemId, this.$i18n.locale, force);
    },
    formatDate(value) {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(this.$i18n.locale, { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>

<style scoped>
.pmw { display: flex; flex-direction: column; gap: 10px; height: 100%; min-height: 0; overflow-y: auto; font-size: 0.84rem; }
.pmw-state { display: flex; align-items: center; gap: 0.5rem; margin: 0; color: var(--dw-muted); }
.pmw-summary { margin: 0; line-height: 1.45; }
.pmw-rank, .pmw-plain { margin: 0; padding-left: 1.1rem; display: flex; flex-direction: column; gap: 3px; line-height: 1.35; }
.pmw-plain { list-style: disc; }
.pmw-muted { font-size: 0.74rem; color: var(--dw-muted); }
.pmw-foot { margin-top: auto; padding-top: 4px; }
.pmw-sources { font-size: 0.76rem; }
</style>
